/**
 * batchSendTile.ts — ONE card, every staged send a live row
 * (owner directive 2026-08-27, Justin's report: "I wanted him to pull up a
 * whole list of emails for me to approve … he would only pull up one email
 * at a time").
 *
 * The server now lets N distinct holds coexist (draft-identity supersede,
 * pendingActions.ts) and stamps every hold a turn stages with one shared
 * batchId. This module is the surface for that: the FIRST marker of a batch
 * posts a single tile; every later marker appends its row and re-renders the
 * SAME message in place (updateStreamingMessageById — race-safe by id).
 * Decisions stamp their receipt INTO the row, so the tile is always the
 * whole truth: N staged · M sent · K awaiting · J skipped.
 *
 * Row buttons reuse the EXISTING authorize-card ids
 * (`authorize-action-approve/cancel-<actionId>` + data attributes), so the
 * existing click handler, its per-id double-click guard, and the CTA
 * exemption (`/^authorize-action-(approve|cancel)/` is a standing decision,
 * never retired) all apply unchanged. "send all remaining" loops the pending
 * rows through the SAME per-item transactional decide — one row's failure
 * stops nothing, and the per-doc claim stays the only double-send guard.
 *
 * Refresh degradation (deliberate, v1): the in-memory registry dies with the
 * page. After a refresh the persisted tile still renders and every row
 * button still works (the server answers each click honestly); the receipt
 * for a post-refresh click lands as a separate receipt message — exactly
 * today's single-card behavior — instead of stamping the row. Server docs
 * are the truth throughout.
 */

import {
  decidePendingAction,
  peekPendingAction,
  type PendingActionOutcome,
} from '../../prest0nSession/client';
import type { MessagingHelpers } from '../types';
import {
  buildStagedSendCardHtml,
  STAGED_SEND_BODY_BUDGET,
  STAGED_SEND_ID_CEILING,
  STAGED_SEND_ROW_CEILING,
  type StagedSendRow,
  type StagedSendRowState,
} from './authorizeCardHtml';

// The row shape and the renderer live in authorizeCardHtml.ts — the
// Firebase-free leaf — because ONE renderer now draws a single staged email
// and a batch of forty (owner directive 2026-09-17: "if there is only one it
// shouldn't look like there are multiple"), and the chat reducer must be able
// to call it. These aliases keep every existing import site unchanged.
export type BatchRowState = StagedSendRowState;
export type BatchRow = StagedSendRow;

interface BatchTileEntry {
  tileMessageId: string | null;
  rows: BatchRow[];
  /** Guards the create race: two markers arriving before the first
   *  addSystemMessage resolves share one creation. */
  creating: Promise<void> | null;
}

/** Messaging surface the tile needs. `updateMessageById` is the in-place
 *  re-render primitive; absent → the caller falls back to single cards. */
export interface BatchTileMessaging {
  addSystemMessage: MessagingHelpers['addSystemMessage'];
  updateMessageById?: (id: string, update: { workflowActionButtonsHtml?: string }) => void;
}

// Per-(thread,batch) registry. Module-level by design: the marker lane and
// the click handler are different call stacks in the same page session.
const registry = new Map<string, BatchTileEntry>();
const registryKey = (threadId: string | null | undefined, batchId: string) =>
  `${threadId || 'nothread'}::${batchId}`;

/** TEST-ONLY: reset module state between cases. */
export function __resetBatchTileRegistry(): void {
  registry.clear();
}

/** Historical name for the row ceiling. The 20-row display cap RETIRED on
 *  2026-09-17 (owner: "it should be able to easily quickly view all emails
 *  suggested within the element") — every staged email is now rendered and
 *  reachable from the jump rail. What remains is a DOM safety ceiling. */
export const BATCH_RENDER_CAP = STAGED_SEND_ROW_CEILING;

/** Re-exported from the renderer so existing importers and pins keep working. */
export const TILE_BODY_DISPLAY_BUDGET = STAGED_SEND_BODY_BUDGET;

/**
 * The tile — a thin wrapper over the ONE staged-send renderer, which draws a
 * single email and a batch of forty from the same code. Pure and idempotent:
 * every render rebuilds from the full row array, never patches html (the
 * anti-race rule).
 */
export function buildBatchSendTileHtml(batchId: string, rowsIn: BatchRow[]): string {
  return buildStagedSendCardHtml({ batchId, rows: rowsIn });
}

/**
 * The ONE mapping from a served preview to what the card draws. Both peek
 * sites (first render and post-refresh rehydrate) go through it, so a field
 * the server starts sending can never reach one surface and miss the other —
 * `peekPendingAction` serializes `preview` whole, so new fields arrive here
 * with no endpoint change.
 */
export function rowFieldsFromPendingPreview(preview: Record<string, any>): Omit<BatchRow, 'actionId' | 'batchIndex' | 'state' | 'note' | 'retryable'> {
  return {
    to: preview.to || '',
    cc: preview.cc || '',
    bcc: preview.bcc || '',
    subject: preview.subject || '',
    from: preview.from || null,
    body: preview.bodyPreview || '',
    bodyTruncated: preview.bodyTruncated === true,
    recipientsTruncated: preview.recipientsTruncated === true,
    attachments: Array.isArray(preview.attachments) ? preview.attachments : undefined,
    attachmentIssues: Array.isArray(preview.attachmentIssues) ? preview.attachmentIssues : undefined,
    lastEditedBy: preview.lastEditedBy?.uid ? preview.lastEditedBy : undefined,
  };
}

export function rowStateFromPeek(peek: {
  status: string;
  confirmation?: { delivered: boolean; kind: string; remediation: string } | null;
  autoApproved?: boolean;
  retryable?: boolean;
}): { state: BatchRowState; note: string | null; retryable: boolean } {
  const c = peek.confirmation;
  if (peek.status === 'executed' && c?.delivered) {
    return { state: peek.autoApproved ? 'auto-sent' : 'sent', note: null, retryable: false };
  }
  if (peek.status === 'cancelled') return { state: 'skipped', note: null, retryable: false };
  if (peek.status === 'superseded') return { state: 'superseded', note: null, retryable: false };
  if (peek.status === 'expired') return { state: 'expired', note: null, retryable: false };
  if (peek.status === 'pending') {
    // A re-armed hold (pending WITH a stamped attempt) is still awaiting.
    return { state: 'pending', note: null, retryable: peek.retryable === true };
  }
  if (c && !c.delivered && c.kind === 'unconfirmed') {
    return { state: 'unconfirmed', note: null, retryable: false };
  }
  return { state: 'failed', note: null, retryable: peek.retryable === true };
}

/**
 * The marker entry point — the batch counterpart of
 * renderAuthorizeCardFromMarker / renderAutoSentReceiptFromMarker. Returns
 * true when the hold joined a batch tile; false lets the caller fall back to
 * the single-card path (no batchId, no updater, peek failed).
 */
export async function renderOrUpdateBatchTile(
  actionId: string,
  threadId: string | null | undefined,
  messaging: BatchTileMessaging,
): Promise<boolean> {
  if (!messaging.updateMessageById) return false;
  let peek: Awaited<ReturnType<typeof peekPendingAction>>;
  try {
    peek = await peekPendingAction(actionId);
  } catch {
    return false;
  }
  const batchId = (peek as any)?.batchId as string | null | undefined;
  if (!peek?.preview || !batchId) return false;
  const { state, note, retryable } = rowStateFromPeek(peek as any);
  // A hold that is already dead on arrival (superseded by a same-draft
  // re-stage in the same turn) renders no row — its replacement will.
  if (state === 'superseded') return true;

  const row: BatchRow = {
    actionId,
    batchIndex: Number((peek as any)?.batchIndex) || 0,
    ...rowFieldsFromPendingPreview(peek.preview as Record<string, any>),
    state,
    note,
    retryable,
  };

  const key = registryKey(threadId, batchId);
  let entry = registry.get(key);
  if (!entry) {
    entry = { tileMessageId: null, rows: [], creating: null };
    registry.set(key, entry);
  }
  const existing = entry.rows.findIndex((r) => r.actionId === actionId);
  if (existing >= 0) entry.rows[existing] = row; else entry.rows.push(row);

  if (!entry.tileMessageId) {
    if (!entry.creating) {
      entry.creating = Promise.resolve().then(() => {
        const message = messaging.addSystemMessage({
          // DATA-ONLY (connect-card convention): the tile is the surface; the
          // orchestrator's own turn says any accompanying sentence.
          renderPolicy: 'never',
          payload: {
            kind: 'prest0n.batch_send_offer',
            toolId: 'connect-app',
            severity: 'info',
            data: { batchId, threadId: threadId || null },
            summary:
              'prest0n: staged sends are HELD — the ONE batch card below carries every staged email in ' +
              'full: sending mailbox, recipient, subject, AND the complete draft body on each row, with its ' +
              'own send/skip buttons and a send-all. The card is the whole message: emit EMPTY content — ' +
              'never restate the drafts, the addressing, or any commentary about them. Only the user’s ' +
              'clicks (or typed decisions) send. NEVER say anything was sent, and never describe or point at ' +
              'the card.',
          } as any,
          sourceTag: 'prest0n_batch_send',
          workflowActionButtonsHtml: buildBatchSendTileHtml(batchId, entry!.rows),
        } as any);
        entry!.tileMessageId = (message as any)?.id || null;
        entry!.creating = null;
      });
    }
    await entry.creating;
  }
  if (entry.tileMessageId) {
    messaging.updateMessageById(entry.tileMessageId, {
      workflowActionButtonsHtml: buildBatchSendTileHtml(batchId, entry.rows),
    });
    return true;
  }
  return false;
}

/** Map a decide outcome onto the row's receipt state. */
export function rowStateFromOutcome(outcome: PendingActionOutcome): { state: BatchRowState; note: string | null; retryable: boolean } {
  if (outcome.delivered) {
    return { state: outcome.autoApproved ? 'auto-sent' : 'sent', note: null, retryable: false };
  }
  if (outcome.kind === 'cancelled') return { state: 'skipped', note: null, retryable: false };
  if (outcome.kind === 'superseded') return { state: 'superseded', note: null, retryable: false };
  if (outcome.kind === 'expired') return { state: 'expired', note: null, retryable: false };
  if (outcome.kind === 'unreachable') {
    // The decision never reached the server — NOTHING was decided, and the
    // claim is transactional, so the row re-arms (same rule as the single
    // card's restoreWorkflowCtaButton re-arm).
    return { state: 'pending', note: 'we could not reach the server — nothing was decided; try again', retryable: true };
  }
  if (outcome.kind === 'unconfirmed') {
    return { state: 'unconfirmed', note: null, retryable: false };
  }
  return {
    state: 'failed',
    // Class copy only — reason is the provider's words when present; our
    // internals never reach a row (same never-leak rule as the receipt card).
    note: outcome.kind === 'internal-error' ? 'a problem on our side, not yours' : (outcome.reason || null),
    retryable: outcome.retryable === true,
  };
}

/**
 * Stamp a decision onto its batch row and re-render the tile. Returns true
 * when the actionId belonged to a registered tile (caller then SKIPS the
 * separate receipt message — the row is the receipt); false → caller falls
 * back to the single-card receipt exactly as today.
 */
export function stampBatchDecision(
  actionId: string,
  outcome: PendingActionOutcome,
  messaging: BatchTileMessaging,
): boolean {
  if (!messaging.updateMessageById) return false;
  for (const [, entry] of registry) {
    const row = entry.rows.find((r) => r.actionId === actionId);
    if (!row || !entry.tileMessageId) continue;
    const mapped = rowStateFromOutcome(outcome);
    row.state = mapped.state;
    row.note = mapped.note;
    row.retryable = mapped.retryable;
    const batchId = registryBatchIdFor(entry);
    messaging.updateMessageById(entry.tileMessageId, {
      workflowActionButtonsHtml: buildBatchSendTileHtml(batchId, entry.rows),
    });
    return true;
  }
  return false;
}

function registryBatchIdFor(entry: BatchTileEntry): string {
  for (const [key, candidate] of registry) {
    if (candidate === entry) return key.slice(key.indexOf('::') + 2);
  }
  return '';
}

/** Mark a row in-flight ('sending…') and re-render — the glance state while
 *  its decide round-trips. */
export function markBatchRowSending(actionId: string, messaging: BatchTileMessaging): void {
  if (!messaging.updateMessageById) return;
  for (const [, entry] of registry) {
    const row = entry.rows.find((r) => r.actionId === actionId);
    if (!row || !entry.tileMessageId) continue;
    if (row.state === 'pending') {
      row.state = 'sending';
      messaging.updateMessageById(entry.tileMessageId, {
        workflowActionButtonsHtml: buildBatchSendTileHtml(registryBatchIdFor(entry), entry.rows),
      });
    }
    return;
  }
}

export interface BatchSendAllSummary {
  total: number;
  sent: number;
  skipped: number;
  failed: number;
  unconfirmed: number;
  /** Rows whose decide never reached the server — re-armed, still awaiting. */
  unreachable: number;
}

/**
 * SEND ALL REMAINING — the master button's engine. Loops every still-pending
 * row through the SAME per-item transactional decide (bounded concurrency;
 * one failure stops nothing), stamping each row as it settles. The per-doc
 * claim makes a re-press after an interruption safe: already-decided rows
 * answer honestly and stamp their true state.
 */
export async function sendAllRemaining(
  batchId: string,
  messaging: BatchTileMessaging,
  onEach?: (actionId: string, outcome: PendingActionOutcome) => void,
): Promise<BatchSendAllSummary | null> {
  let found: BatchTileEntry | null = null;
  for (const [key, entry] of registry) {
    if (key.endsWith(`::${batchId}`)) { found = entry; break; }
  }
  if (!found) return null;
  const targets = found.rows
    .filter((r) => r.state === 'pending')
    .sort((a, b) => a.batchIndex - b.batchIndex);
  const summary: BatchSendAllSummary = { total: targets.length, sent: 0, skipped: 0, failed: 0, unconfirmed: 0, unreachable: 0 };

  const CONCURRENCY = 3;
  let cursor = 0;
  const worker = async () => {
    while (cursor < targets.length) {
      const row = targets[cursor];
      cursor += 1;
      markBatchRowSending(row.actionId, messaging);
      let outcome: PendingActionOutcome;
      try {
        outcome = await decidePendingAction(row.actionId, 'authorize');
      } catch {
        outcome = {
          ok: false, decision: 'authorize', status: 'unreachable',
          delivered: false, kind: 'unreachable',
        } as PendingActionOutcome;
      }
      if (outcome.delivered) summary.sent += 1;
      else if (outcome.kind === 'unreachable') summary.unreachable += 1;
      else if (outcome.kind === 'unconfirmed') summary.unconfirmed += 1;
      else if (outcome.kind === 'cancelled') summary.skipped += 1;
      else summary.failed += 1;
      stampBatchDecision(row.actionId, outcome, messaging);
      if (onEach) onEach(row.actionId, outcome);
    }
  };
  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, targets.length) }, worker));
  return summary;
}

// ─────────────────────────────────────────────────────────────────────────────
// REHYDRATION (refresh self-healing, plan D2's belt): the registry dies with
// the page, but the tile's persisted html carries its own self-description
// (data-batch-root + data-batch-action-ids) and the server holds the truth
// for every row. Re-reading each hold through the SAME peek endpoint the
// live path uses rebuilds the registry — rows stamp in place again, send-all
// works again, and a tile that went stale (rows decided on another device or
// before the refresh) re-renders honest.
// ─────────────────────────────────────────────────────────────────────────────

/** Parse a persisted tile's self-description out of its html. Null when the
 *  html predates the attributes (pre-rehydration deploys) — those tiles keep
 *  the documented v1 fallback (separate receipt messages). */
export function parseBatchTileHtml(html: string | null | undefined): { batchId: string; actionIds: string[] } | null {
  const src = String(html || '');
  // The builder still emits the two attributes adjacent (old shipped bundles
  // regex exactly that), but THIS parser tolerates anything between them
  // within the same tag, so a future attribute reorder can't kill
  // rehydration of already-persisted tiles.
  const root = /data-batch-root="([^"]+)"[^>]*?data-batch-action-ids="([^"]*)"/.exec(src);
  if (!root || !root[1]) return null;
  // Bounded by the SAME ceiling the renderer draws to, in lockstep — a tile
  // that rendered 200 rows must be able to rehydrate all 200.
  const actionIds = root[2].split(',').map((s) => s.trim()).filter(Boolean).slice(0, STAGED_SEND_ID_CEILING);
  if (actionIds.length === 0) return null;
  return { batchId: root[1], actionIds };
}

/** One rehydrate flight per tile message per page session — a second caller
 *  awaits the first instead of double-peeking. */
const rehydrating = new Map<string, Promise<boolean>>();

/** TEST-ONLY companion to __resetBatchTileRegistry. */
export function __resetBatchTileRehydration(): void {
  rehydrating.clear();
}

/**
 * Rebuild a persisted tile's registry entry from the server's truth and
 * re-render it in place. Idempotent and race-guarded per messageId. Returns
 * true when the tile is live again (rows stamp, send-all works); false when
 * nothing could be rebuilt (old html, every peek failed, no updater) — the
 * caller keeps the v1 fallback behavior.
 */
export async function rehydrateBatchTile(params: {
  messageId: string;
  threadId: string | null | undefined;
  html: string | null | undefined;
  messaging: BatchTileMessaging;
}): Promise<boolean> {
  const { messageId, threadId, html, messaging } = params;
  if (!messaging.updateMessageById || !messageId) return false;
  const parsed = parseBatchTileHtml(html);
  if (!parsed) return false;
  const key = registryKey(threadId, parsed.batchId);
  const existing = registry.get(key);
  // A live entry for this same message needs no rebuild; a live entry bound
  // to a DIFFERENT message means a fresh batch reused the id (never happens
  // with minted ids — but never clobber live state on a collision).
  if (existing && existing.tileMessageId) return existing.tileMessageId === messageId;

  const inFlight = rehydrating.get(messageId);
  if (inFlight) return inFlight;
  const flight = (async (): Promise<boolean> => {
    const rows: BatchRow[] = [];
    const CONCURRENCY = 4;
    let cursor = 0;
    const worker = async () => {
      while (cursor < parsed.actionIds.length) {
        const actionId = parsed.actionIds[cursor];
        cursor += 1;
        try {
          const peek = await peekPendingAction(actionId);
          if (!peek?.preview) continue;
          const mapped = rowStateFromPeek(peek as any);
          rows.push({
            actionId,
            batchIndex: Number((peek as any)?.batchIndex) || 0,
            ...rowFieldsFromPendingPreview(peek.preview as Record<string, any>),
            state: mapped.state,
            note: mapped.note,
            retryable: mapped.retryable,
          });
        } catch { /* one unreadable hold never sinks the tile */ }
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, parsed.actionIds.length) }, worker));
    if (rows.length === 0) return false;
    registry.set(key, { tileMessageId: messageId, rows, creating: null });
    messaging.updateMessageById!(messageId, {
      workflowActionButtonsHtml: buildBatchSendTileHtml(parsed.batchId, rows),
    });
    return true;
  })();
  rehydrating.set(messageId, flight);
  try {
    return await flight;
  } finally {
    // Keep only a SUCCESSFUL flight cached; a failed one may retry later
    // (e.g. the network came back before the user clicked).
    void flight.then((ok) => { if (!ok) rehydrating.delete(messageId); });
  }
}

/**
 * Click-time fallback: rebuild the tile from the clicked button's own DOM
 * (tile root attributes + the message element's data-message-id) when the
 * registry has no entry — the post-refresh path where no hydrate scan ran.
 */
export async function rehydrateBatchTileFromDom(
  buttonEl: HTMLElement | null | undefined,
  threadId: string | null | undefined,
  messaging: BatchTileMessaging,
): Promise<boolean> {
  if (!buttonEl) return false;
  const root = buttonEl.closest('[data-batch-root]') as HTMLElement | null;
  const messageEl = buttonEl.closest('[data-message-id]') as HTMLElement | null;
  const batchId = root?.getAttribute('data-batch-root') || '';
  const idsRaw = root?.getAttribute('data-batch-action-ids') || '';
  const messageId = messageEl?.getAttribute('data-message-id') || '';
  if (!batchId || !idsRaw || !messageId) return false;
  return rehydrateBatchTile({
    messageId,
    threadId,
    // Synthesize the minimal self-description the parser reads.
    html: `data-batch-root="${batchId}" data-batch-action-ids="${idsRaw}"`,
    messaging,
  });
}

/** True when this actionId already has a live row on some tile. */
export function hasBatchRow(actionId: string): boolean {
  for (const [, entry] of registry) {
    if (entry.tileMessageId && entry.rows.some((r) => r.actionId === actionId)) return true;
  }
  return false;
}

/** True when this batchId has a live registry entry. */
export function hasBatchEntry(batchId: string): boolean {
  for (const [key, entry] of registry) {
    if (key.endsWith(`::${batchId}`) && entry.tileMessageId) return true;
  }
  return false;
}
